import { Button, Container, Spinner, Stack, useDisclosure, useToast } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { FreightService } from '@prisma/client';
import { useCallback, useEffect, useMemo } from 'react';
import { HiChevronLeft, HiShare } from 'react-icons/hi2';
import { Link as RRLink, useNavigate, useParams } from 'react-router-dom';

import { AfrBooking } from '../../components/Booking/AfrBooking';
import { ExwBooking } from '../../components/Booking/ExwBooking';
import { FcaBooking } from '../../components/Booking/FcaBooking';
import { ForceNavModal } from '../../components/ForceNavModal';
import { Page } from '../../components/Page';
import { trpc, TrpcOutputs } from '../../config/trpc';
import { SelectedQuoteVariantProvider } from '../../context/SelectedQuoteVariantContext';
import { CONTAINER_2XL } from '../../utils/constants';

type Props = {
  quote: TrpcOutputs['getQuote'];
  request: TrpcOutputs['getQuote']['request'];
};

const BookQuoteUI = ({ quote, request }: Props) => {
  const toast = useToast();

  const handleShare = async () => {
    try {
      if (!navigator.share) throw new Error('Share not supported');
      await navigator.share({
        title: `${quote.provider_id} booking for ${quote.origin_airport_id} -> ${quote.destination_airport_id}`,
        url: window.location.href,
      });
    } catch {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Success',
        description: 'Link copied to clipboard!',
        status: 'success',
      });
    }
  };

  const deleteModal = useDisclosure();

  trpc.onQuoteDeleted.useSubscription(
    {
      requestId: request?.id,
    },
    {
      onData: (quoteId) => {
        if (quoteId === quote?.id) {
          deleteModal.onOpen();
        }
      },
    },
  );

  return (
    <Page title="Book Quote">
      <Container
        w="full"
        maxW={CONTAINER_2XL}
        pt={{ base: '8', lg: '10' }}
        pb={{ base: '8', lg: '20' }}
        px={{ base: '4', lg: '12' }}
      >
        <ForceNavModal
          {...deleteModal}
          body="This quote was deleted or doesn't exist, please return to the quote list."
          title="Quote no longer exists"
          buttonText="Return to Quotes Listing"
          to={`/requests/${request?.id}`}
        />

        <Stack spacing="6">
          <Stack justify="space-between" direction="row">
            <Button
              as={RRLink}
              variant="outline"
              size="sm"
              leftIcon={<HiChevronLeft />}
              to={`/requests/${quote?.request?.id}`}
            >
              Quotes
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<HiShare />} onClick={handleShare}>
              Share Listing
            </Button>
          </Stack>
          <Show if={!quote}>
            <Stack align="center" justify="center" h="60vh">
              <Spinner />
            </Stack>
          </Show>
          <Show if={request?.freight_services?.includes(FreightService.EXW)}>
            <ExwBooking request={request} quote={quote} />
          </Show>
          <Show if={request?.freight_services?.includes(FreightService.FCA)}>
            <FcaBooking request={request} quote={quote} />
          </Show>
          <Show if={request?.freight_services?.every((service) => service === FreightService.AFR)}>
            <AfrBooking request={request} quote={quote} />
          </Show>
        </Stack>
      </Container>
    </Page>
  );
};

export const BookQuote = () => {
  const params = useParams<{ requestId: string; quoteId: string; quoteVariantId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: quote, refetch } = trpc.getQuote.useQuery({ id: Number(params?.quoteId) });
  trpc.onQuoteUpdated.useSubscription(
    {
      requestId: parseInt(params?.requestId),
    },
    {
      enabled: !!params?.requestId,
      onData: (data) => {
        if (data?.id === quote?.id) {
          refetch();
        }
      },
    },
  );

  const request = useMemo(() => {
    return quote?.request;
  }, [quote?.request]);

  const quoteVariant = useMemo(() => {
    return quote?.quote_variants.find((variant) => variant.id === Number(params?.quoteVariantId));
  }, [quote?.quote_variants, params?.quoteVariantId]);

  // TODO: implement finding cheapest
  const cheapestQuoteVariant = useMemo(() => {
    return quote?.quote_variants?.[0];
  }, [quote?.quote_variants]);

  const buildUrl = useCallback(
    (qvId: string | number) => `/requests/${request?.id}/quotes/${quote?.id}/${qvId}`,
    [request?.id, quote?.id],
  );

  useEffect(() => {
    // If there's no quoteVariant in the component, then we need to find it
    if (!quoteVariant?.id) {
      // Check if we have a desired QV ID in the URL params and that it's valid for this quote
      const hasValidQvIdInParams =
        !!params?.quoteVariantId &&
        quote?.quote_variants?.some((variant) => variant.id === Number(params?.quoteVariantId));
      if (hasValidQvIdInParams) {
        return navigate(buildUrl(params.quoteVariantId), { replace: true });
      }

      // Check if we have a cheapest qv id to redirect to
      if (cheapestQuoteVariant?.id) {
        return navigate(buildUrl(cheapestQuoteVariant?.id), { replace: true });
      }
    }
  }, [quoteVariant?.id, params?.quoteVariantId, navigate, buildUrl, quote?.quote_variants, cheapestQuoteVariant?.id]);

  return (
    <SelectedQuoteVariantProvider>
      <BookQuoteUI quote={quote} request={request} />;
    </SelectedQuoteVariantProvider>
  );
};
